<template>
  <section class="home">
    <div class="home__hero">
      <img class="home__emblem" src="@/assets/images/knb_logo.png" alt="Пограничная служба" />
      <h1 class="home__title">Пограничная служба КНБ РК</h1>
      <p class="home__subtitle">
        Интегрированная система автоматизированного управления САКШЫ
        <template v-if="userName"> · <span class="home__user">{{ userName }}</span></template>
      </p>
    </div>

    <div class="tiles">
      <button
        v-for="tile in tiles"
        :key="tile.to"
        type="button"
        class="tile"
        @click="go(tile.to)"
      >
        <span class="tile__icon" :style="{ background: tile.color }">
          <LucideIcon :size="28" strokeWidth="1.75"><g v-html="tile.iconSvg" /></LucideIcon>
        </span>
        <span class="tile__body">
          <span class="tile__label">{{ tile.label }}</span>
          <span v-if="tile.desc" class="tile__desc">{{ tile.desc }}</span>
        </span>
      </button>
    </div>
  </section>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue'
import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import {
  canAccessModule,
  canAccessPath,
  MODULE_CHPO, MODULE_KPS, MODULE_HOUSEHOLD,
  MODULE_BSFP, MODULE_VIR, MODULE_MPO,
} from '@/access/armAccess'

// Иконки skopirovany из PlanNavigation для консистентности
const IconBook = '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/>'
const IconWarehouse = '<path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35a2 2 0 0 1 1.26-1.86l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/><rect width="12" height="12" x="6" y="10"/>'
const IconMap = '<polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6"/><line x1="9" x2="9" y1="3" y2="18"/><line x1="15" x2="15" y1="6" y2="21"/>'
const IconRadar = '<path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"/><path d="M4 6h.01"/><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"/><path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"/><path d="M12 18h.01"/><path d="M17.99 11.66A6 6 0 0 1 15.77 16.67"/><circle cx="12" cy="12" r="2"/><path d="m13.41 10.59 5.66-5.66"/>'
const IconActivity = '<path d="M6.5 6.5h11"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/><circle cx="12" cy="12" r="3"/>'
const IconUsers = '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'
const IconStar = '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>'
const IconShield = '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>'
const IconClipboard = '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>'

export default {
  name: 'HomeView',
  components: { LucideIcon },
  data() {
    return { currentUser: null }
  },
  computed: {
    armUser() { return this.currentUser || getCachedCurrentUser() },
    userName() {
      const u = this.armUser
      if (!u) return ''
      return u.full_name || ''
    },
    isAdmin() {
      const u = this.armUser
      return !!(u && (u.is_superuser || u.is_staff))
    },
    tiles() {
      const u = this.armUser
      const all = [
        {
          label: 'Книга пограничной службы',
          desc: 'Карта, личный состав, приказы, разделы 4–14',
          to: '/kps/map',
          iconSvg: IconBook,
          color: '#1D4ED8',
          show: () => canAccessModule(u, MODULE_KPS),
        },
        {
          label: 'Составление плана охраны',
          desc: 'Принятие решения на очередные сутки',
          to: '/chpo/decision-start',
          iconSvg: IconShield,
          color: '#15803D',
          show: () => canAccessModule(u, MODULE_CHPO),
        },
        {
          label: 'Войсковое хозяйство',
          desc: 'Номенклатуры, склады, операции, отчёты',
          to: '/household/nomenclatures',
          iconSvg: IconWarehouse,
          color: '#B45309',
          show: () => canAccessModule(u, MODULE_HOUSEHOLD),
        },
        {
          label: 'Схема участка',
          desc: 'Редактирование топографической карты',
          to: '/map-scheme',
          iconSvg: IconMap,
          color: '#6D28D9',
          show: () => canAccessPath(u, '/map-scheme'),
        },
        {
          label: 'ТСОГ',
          desc: 'Технические средства охраны границы',
          to: '/tsog',
          iconSvg: IconRadar,
          color: '#0E7490',
          show: () => canAccessPath(u, '/tsog'),
        },
        {
          label: 'БСФП',
          desc: 'Боевая и служебная физ. подготовка',
          to: '/bsfp/attendance',
          iconSvg: IconActivity,
          color: '#BE185D',
          show: () => canAccessModule(u, MODULE_BSFP),
        },
        {
          label: 'ВИР',
          desc: 'Воспитательно-идеологическая работа',
          to: '/vir/performance',
          iconSvg: IconUsers,
          color: '#9333EA',
          show: () => canAccessModule(u, MODULE_VIR),
        },
        {
          label: 'МПО',
          desc: 'Морально-психологическое обеспечение',
          to: '/mpo',
          iconSvg: IconStar,
          color: '#CA8A04',
          show: () => canAccessModule(u, MODULE_MPO),
        },
        {
          label: 'Пользователи системы',
          desc: 'Создание и управление учётками',
          to: '/admin/users',
          iconSvg: IconClipboard,
          color: '#374151',
          show: () => this.isAdmin,
        },
      ]
      return all.filter(t => t.show())
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
  },
  methods: {
    go(path) { if (path) this.$router.push(path) },
  },
}
</script>

<style scoped>
.home {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-height: 100%;
}

.home__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0 8px;
  text-align: center;
}

.home__emblem {
  width: 220px;
  height: 220px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08));
}

.home__title {
  margin: 0;
  color: #111827;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.home__subtitle {
  margin: 0;
  color: #6B7280;
  font-size: 15px;
  line-height: 1.5;
  max-width: 720px;
}

.home__user { color: #2F78EE; font-weight: 600; }

.tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tile {
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  min-width: 0;
  font-family: inherit;
}
.tile:hover {
  transform: translateY(-2px);
  border-color: #2F78EE;
  box-shadow: 0 6px 16px rgba(47, 120, 238, 0.12);
}
.tile:active { transform: translateY(0); }

.tile__icon {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #fff;
}

.tile__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
}

.tile__label {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
}

.tile__desc {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.35;
}

@media (max-width: 1180px) {
  .tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 720px) {
  .tiles { grid-template-columns: 1fr; }
  .home__emblem { width: 160px; height: 160px; }
  .home__title { font-size: 24px; }
}
</style>
